import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import App from '../App';
import LoginPage from './LoginPage';
import AuthCallback from './AuthCallback';
import EmergencyReset from './EmergencyReset';
// Import AuthProvider and useAuth hook
import { AuthProvider, useAuth } from '../context/AuthContext';

/**
 * Protected Route Component
 * Ensures routes are only accessible when authenticated
 * Uses the AuthContext to manage authentication state across the app
 */
const ProtectedRoute = ({ element }) => {
  // Use the auth context instead of local state
  const { loading, isAuthenticated } = useAuth();

  // Show loading indicator while auth state is being checked
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated, otherwise show the requested element
  return isAuthenticated ? element : <Navigate to="/login" />;
};

/**
 * App Router Component
 * Manages application routing and authentication flows
 * Wraps the entire application with the AuthProvider
 */
const AppRouter = () => {
  // Layout component that provides the auth context
  const AuthLayout = () => (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );

  // Define routes with the createBrowserRouter function
  const router = createBrowserRouter([
    {
      element: <AuthLayout />,
      children: [
        {
          path: "/",
          element: <ProtectedRoute element={<App />} />
        },
        {
          path: "/tickets",
          element: <ProtectedRoute element={<App />} />
        },
        {
          path: "/login",
          element: <LoginPage />
        },
        {
          path: "/auth/callback",
          element: <AuthCallback />
        },
        {
          path: "/emergency-reset",
          element: <EmergencyReset />
        },
        {
          path: "*",
          element: <Navigate to="/" replace />
        }
      ]
    }
  ]);

  return <RouterProvider router={router} />;
};

export default AppRouter;
